import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import * as Highcharts from 'highcharts';

import { ActionPlanConstants } from 'app/constants/constants';
import { ActionPlan } from 'app/providers/storage-api/actionplan.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-status-progress-view',
  templateUrl: './status-progress-view.component.html',
  styleUrls: ['./status-progress-view.component.scss'],
  standalone: false,
})
export class StatusProgressViewComponent implements OnInit, AfterViewInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() actionPlan: ActionPlan;
  @Input() mode: 'item' | 'task';

  @ViewChild('chart', { static: false })
  chartElement: ElementRef<HTMLDivElement>;

  chartObject: Highcharts.Chart;

  totalCount = 0;
  notStartedCount = 0;
  inProgressCount = 0;
  completedCount = 0;
  notStartedPct = 0;
  inProgressPct = 0;
  completedPct = 0;

  readonly constants = ActionPlanConstants;

  constructor(private translocoService: TranslocoService) {}

  ngOnInit(): void {
    this.setCounts();
  }

  ngAfterViewInit(): void {
    this.drawChart();
  }

  private setCounts(): void {
    switch (this.mode) {
      case 'item':
        this.totalCount = this.actionPlan.total_child_count;
        this.notStartedCount = this.actionPlan.new_child_count;
        this.inProgressCount = this.actionPlan.in_progress_child_count;
        this.completedCount = this.actionPlan.completed_child_count;
        break;
      case 'task':
        (this.actionPlan.action_items ?? []).forEach((i) => {
          this.totalCount += i.total_child_count;
          this.notStartedCount += i.new_child_count;
          this.inProgressCount += i.in_progress_child_count;
          this.completedCount += i.completed_child_count;
        });
        break;
    }

    if (this.totalCount) {
      this.notStartedPct = Utils.round((this.notStartedCount * 100) / this.totalCount, 0);
      this.inProgressPct = Utils.round((this.inProgressCount * 100) / this.totalCount, 0);
      this.completedPct = Utils.round((this.completedCount * 100) / this.totalCount, 0);
    }
  }

  private drawChart(): void {
    const that = this;

    const options: Highcharts.Options = {
      chart: {
        type: 'pie',
        margin: [-8, -8, -8, -8], // to fill full size of container
        events: {
          render() {
            const chart = this as any; // necessary
            const color = '#2D3A44';
            const fontFamily = 'IBM Plex Sans';

            const x = chart.plotLeft + chart.series[0].center[0];
            const y = chart.plotTop + chart.series[0].center[1];

            chart.pct?.destroy();
            chart.text?.destroy();

            chart.pct = chart.renderer
              .text(`${that.completedPct}%`, x, y - 5, true)
              .css({
                color,
                fontSize: '20px',
                fontWeight: '700',
                fontFamily,
              })
              .add();
            chart.pct.attr({
              x: x - chart.pct.getBBox().width / 2,
            });

            chart.text = chart.renderer
              .text(
                that.translocoService.translateObject('status-progress-view.chart-completed'),
                x,
                y + 15,
                true
              )
              .css({
                color,
                fontSize: '16px',
                fontFamily,
              })
              .add();
            chart.text.attr({
              x: x - chart.text.getBBox().width / 2,
            });
          },
        },
      },
      series: [
        {
          type: 'pie',
          innerSize: '85%',
          dataLabels: {
            enabled: false,
          },
          data: [
            {
              y: this.notStartedCount,
              color: this.constants.NOT_STARTED_COLOR,
            },
            {
              y: this.inProgressCount,
              color: this.constants.IN_PROGRESS_COLOR,
            },
            {
              y: this.completedCount,
              color: this.constants.COMPLETED_COLOR,
            },
          ],
        },
      ],
      title: {
        text: '',
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          states: {
            hover: {
              enabled: false,
            },
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    };

    this.chartObject = Highcharts.chart(this.chartElement.nativeElement, options);
  }
}
